import { type ReactElement } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ClipboardList,
  FileSignature,
  PenLine,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';

import { ErroApi } from '@/nucleo/auth/cliente-api';
import { useOrdensPorTermo } from '@/modulos/portal-credenciado/ordens-servico/dados';
import { OsVinculadasDoTermo } from '@/modulos/portal-credenciado/ordens-servico/OsVinculadasDoTermo';
import { EstadoDaTela } from '@/ui/EstadoDaTela';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { ErrorState } from '@/ui/ErrorState';
import { PageHeader } from '@/ui/PageHeader';
import { StatusBadge } from '@/ui/StatusBadge';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';

import {
  ROTULO_CONVITE,
  ROTULO_IMPEDIMENTO,
  ROTULO_PAPEL,
  ROTULO_STATUS_TERMO,
  VARIANTE_CONVITE,
  VARIANTE_STATUS_TERMO,
  dataHoraLegivel,
  dataLegivel,
  numeroExibido,
  type MeuTermoDetalhe,
} from './contrato';
import { mensagemDoErro, useMeuTermo, useMinhaAssinatura } from './dados';

/** As abas do detalhe. `?aba=os` abre direto nas OS (painel e e-mails futuros). */
const ABAS = ['assinaturas', 'documento', 'os'] as const;
type Aba = (typeof ABAS)[number];

function abaValida(valor: string | null): Aba {
  return (ABAS as readonly string[]).includes(valor ?? '') ? (valor as Aba) : 'assinaturas';
}

function Conteudo({ t }: { t: MeuTermoDetalhe }): ReactElement {
  const [parametros, definirParametros] = useSearchParams();
  const aba = abaValida(parametros.get('aba'));
  const escolherAba = (valor: string) =>
    definirParametros(
      (atuais) => {
        const proximos = new URLSearchParams(atuais);
        if (valor === 'assinaturas') proximos.delete('aba');
        else proximos.set('aba', valor);
        return proximos;
      },
      { replace: true },
    );
  const quantasOs = useOrdensPorTermo(t.id).dados?.length ?? 0;
  const assinatura = useMinhaAssinatura(t.id);
  const meu = t.meuConvite;
  const podeAssinar = meu?.papel === 'credenciado' && meu.status === 'PENDENTE' && !meu.impedimento;
  const noProvedor = meu?.status === 'NO_PROVEDOR';

  return (
    <div className="space-y-4">
      <PageHeader
        title={`Termo de Adesão nº ${numeroExibido(t)}`}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <StatusBadge
              status={VARIANTE_STATUS_TERMO[t.status]}
              label={ROTULO_STATUS_TERMO[t.status]}
            />
            <span>
              Vigência de {dataLegivel(t.dataInicio)} a {dataLegivel(t.dataFim)}
            </span>
          </span>
        }
      >
        {(podeAssinar || noProvedor) && (
          <div className="flex flex-wrap gap-2">
            {podeAssinar && (
              <ConfirmDialog
                trigger={
                  <Button size="sm" disabled={assinatura.isPending}>
                    <PenLine className="mr-1 h-4 w-4" aria-hidden="true" />
                    Assinar termo
                  </Button>
                }
                title="Assinar o Termo de Adesão?"
                description="Você será encaminhado ao provedor de assinatura digital. Assine só depois de ler o documento inteiro."
                confirmLabel="Li e quero assinar"
                onConfirm={() =>
                  assinatura.mutate('assinar', {
                    onSuccess: () =>
                      toast.success('Assinatura encaminhada ao provedor de assinatura digital.'),
                  })
                }
              />
            )}
            {noProvedor && (
              <Button
                size="sm"
                variant="outline"
                disabled={assinatura.isPending}
                onClick={() =>
                  assinatura.mutate('atualizar-assinatura', {
                    onSuccess: () => toast.success('Situação da assinatura atualizada.'),
                  })
                }
              >
                <RefreshCw className="mr-1 h-4 w-4" aria-hidden="true" />
                Atualizar situação da assinatura
              </Button>
            )}
          </div>
        )}
      </PageHeader>

      {assinatura.error && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>
            {mensagemDoErro(assinatura.error, 'Não foi possível concluir. Tente de novo.')}
          </AlertDescription>
        </Alert>
      )}

      {meu?.impedimento && (
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Assinatura impedida</AlertTitle>
          <AlertDescription>
            {ROTULO_IMPEDIMENTO[meu.impedimento]} Procure o DTP para regularizar.
          </AlertDescription>
        </Alert>
      )}

      {t.status === 'ATIVO' && (
        <Alert>
          <AlertTitle>Termo ativo</AlertTitle>
          <AlertDescription>
            O Portal do Credenciado está liberado por completo. O próximo passo é a Ordem de
            Serviço, emitida pelo DTP. As Ordens de Serviço ativas aparecem na aba OS vinculadas.
          </AlertDescription>
        </Alert>
      )}

      <Tabs value={aba} onValueChange={escolherAba}>
        <TabsList aria-label="Partes do termo">
          <TabsTrigger value="assinaturas">
            <PenLine className="h-4 w-4" aria-hidden="true" />
            Assinaturas
          </TabsTrigger>
          <TabsTrigger value="documento">
            <FileSignature className="h-4 w-4" aria-hidden="true" />
            Documento
          </TabsTrigger>
          <TabsTrigger value="os">
            <ClipboardList className="h-4 w-4" aria-hidden="true" />
            OS vinculadas ({quantasOs})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="assinaturas">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Assinaturas</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="grid gap-3 sm:grid-cols-2">
                {t.assinaturas.map((a) => (
                  <li key={a.conviteId} className="rounded-md border p-3 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-medium">
                        {a.papel === 'credenciado' ? 'Sua assinatura' : ROTULO_PAPEL[a.papel]}
                      </span>
                      <StatusBadge
                        status={VARIANTE_CONVITE[a.status]}
                        label={ROTULO_CONVITE[a.status]}
                      />
                    </div>
                    <p className="text-muted-foreground text-xs">
                      {a.assinadoEm
                        ? `Assinado em ${dataHoraLegivel(a.assinadoEm)}`
                        : a.status === 'PENDENTE' || a.status === 'NO_PROVEDOR'
                          ? `Prazo: ${dataHoraLegivel(a.expiraEm)}`
                          : (a.motivo ?? '')}
                    </p>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documento" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Objeto</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="wrap-break-word text-sm">{t.objeto ?? '—'}</p>
            </CardContent>
          </Card>

          {t.versao && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <FileSignature className="h-4 w-4" aria-hidden="true" />
                  Documento — versão {t.versao.versao}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-muted-foreground break-all text-xs">
                  Resumo criptográfico (SHA-256): <span className="font-mono">{t.versao.hash}</span>
                </p>
                <pre className="bg-muted/40 max-h-96 overflow-y-auto whitespace-pre-wrap wrap-break-word rounded-md p-3 font-sans text-sm">
                  {t.versao.documento}
                </pre>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="os">
          <Card>
            <CardContent className="p-4">
              <OsVinculadasDoTermo
                termoId={t.id}
                rotulo={`Ordens de Serviço do termo ${numeroExibido(t)}`}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * `/credenciado/contratos/:id` — conferir e assinar o Termo de Adesão e ver as
 * Ordens de Serviço dele. Um `TabsContent` para cada `TabsTrigger`: aba sem
 * painel deixa `aria-controls` apontando para o nada. "Rateio mensal" e
 * "Histórico" do protótipo não vêm: não há API que os sirva (regra 7).
 */
export function ContratoDetailPage(): ReactElement {
  const { id = '' } = useParams();
  const consulta = useMeuTermo(id);
  const naoExiste = consulta.error instanceof ErroApi && consulta.error.situacao === 404;

  return (
    <div className="min-w-0 space-y-4">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/credenciado/contratos">
          <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" />
          Voltar para os termos
        </Link>
      </Button>
      {naoExiste ? (
        <ErrorState
          variant="resource-not-found"
          title="Termo não encontrado"
          primaryAction={{ label: 'Voltar para os termos', to: '/credenciado/contratos' }}
        />
      ) : (
        <EstadoDaTela
          carregando={consulta.isPending}
          erro={consulta.error ?? null}
          aoTentarDeNovo={() => void consulta.refetch()}
          descricao="o termo"
        >
          {consulta.data && <Conteudo t={consulta.data} />}
        </EstadoDaTela>
      )}
    </div>
  );
}

export default ContratoDetailPage;
